/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { isDemoMode } from '../services';

interface PromotionsSummary {
  overview: {
    active_campaigns: number;
    total_reach: number;
    total_spend: number;
    overall_roas: number;
    average_engagement: number;
    communication_recall: number;
  };
  top_channel: string;
  content_performance: {
    total_assets: number;
    avg_engagement: number;
    avg_consistency: number;
    top_emotion: string;
  };
  recommendations: string[];
}

interface Campaign {
  campaign_id: string;
  name: string;
  status: string;
  channel: string;
  engagement_score: number;
  emotional_tone: string;
  reach: number;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
}

interface Channel {
  channel: string;
  impressions: number;
  reach: number;
  engagement_rate: number;
  share_of_voice: number;
  cpc: number;
  roas: number;
  spend: number;
  conversions: number;
}

interface ContentItem {
  content_id: string;
  type: string;
  emotional_tone: string;
  target_audience: string;
  message_clarity: string;
  visual_signals: any;
  engagement_score: number;
  consistency_score: number;
}

export function usePromotions(brandId: string) {
  const [summary, setSummary] = useState<PromotionsSummary | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId) return;

    const fetchPromotionsData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (isDemoMode) {
          // Use mock data in demo mode
          const mockSummary: PromotionsSummary = {
            overview: {
              active_campaigns: 5,
              total_reach: 2500000,
              total_spend: 150000,
              overall_roas: 3.8,
              average_engagement: 4.5,
              communication_recall: 68
            },
            top_channel: 'Instagram',
            content_performance: {
              total_assets: 24,
              avg_engagement: 5.2,
              avg_consistency: 0.78,
              top_emotion: 'Joyful'
            },
            recommendations: [
              'Increase video content on TikTok for younger demographics',
              'Maintain consistent brand messaging across channels',
              'Consider influencer partnerships for higher reach'
            ]
          };

          const mockChannels: Channel[] = [
            { channel: 'Instagram', impressions: 1200000, reach: 800000, engagement_rate: 5.2, share_of_voice: 35, cpc: 0.45, roas: 4.2, spend: 45000, conversions: 12500 },
            { channel: 'Facebook', impressions: 900000, reach: 600000, engagement_rate: 3.8, share_of_voice: 28, cpc: 0.38, roas: 3.5, spend: 35000, conversions: 8200 },
            { channel: 'TikTok', impressions: 750000, reach: 500000, engagement_rate: 7.1, share_of_voice: 22, cpc: 0.52, roas: 4.8, spend: 30000, conversions: 9800 },
            { channel: 'YouTube', impressions: 450000, reach: 350000, engagement_rate: 4.2, share_of_voice: 15, cpc: 0.65, roas: 3.2, spend: 25000, conversions: 5400 }
          ];

          const mockContent = {
            total_items: 24,
            average_consistency: 0.78,
            emotional_tone_distribution: {
              'Joyful': 35,
              'Inspiring': 28,
              'Trustworthy': 22,
              'Exciting': 15
            },
            top_keywords: [
              { keyword: 'refreshing', frequency: 45 },
              { keyword: 'quality', frequency: 38 },
              { keyword: 'taste', frequency: 32 },
              { keyword: 'natural', frequency: 28 }
            ],
            brand_health_context: {
              trust_correlation: 0.72,
              quality_correlation: 0.68,
              consistency_loyalty_correlation: 0.65,
              current_brand_metrics: {
                trust: 0.75,
                quality: 0.80,
                nps: 52
              }
            },
            content_items: [
              { content_id: '1', type: 'Video Ad', emotional_tone: 'Joyful', target_audience: 'Young Adults 18-35', engagement_score: 0.82, consistency_score: 0.85 },
              { content_id: '2', type: 'Social Post', emotional_tone: 'Inspiring', target_audience: 'Health Conscious', engagement_score: 0.75, consistency_score: 0.78 },
              { content_id: '3', type: 'Banner Ad', emotional_tone: 'Exciting', target_audience: 'Sports Enthusiasts', engagement_score: 0.68, consistency_score: 0.72 },
              { content_id: '4', type: 'Story', emotional_tone: 'Trustworthy', target_audience: 'Families', engagement_score: 0.71, consistency_score: 0.80 },
              { content_id: '5', type: 'Carousel', emotional_tone: 'Joyful', target_audience: 'Gen Z', engagement_score: 0.88, consistency_score: 0.76 },
              { content_id: '6', type: 'Reel', emotional_tone: 'Exciting', target_audience: 'Young Adults', engagement_score: 0.92, consistency_score: 0.82 }
            ],
            message: 'Demo mode - showing sample content data'
          };

          setSummary(mockSummary);
          setCampaigns([]);
          setChannels(mockChannels);
          setContent(mockContent);
        } else {
          // Fetch all promotion data in parallel
          const [summaryRes, campaignsRes, channelsRes, contentRes] = await Promise.all([
            axios.get(`/api/promotions/summary/${brandId}`),
            axios.get(`/api/promotions/campaigns/${brandId}`),
            axios.get(`/api/promotions/channels/${brandId}`),
            axios.get(`/api/promotions/content/${brandId}`)
          ]);

          setSummary(summaryRes.data);
          setCampaigns(campaignsRes.data.campaigns || []);
          setChannels(channelsRes.data.channels || []);
          setContent(contentRes.data);
        }
      } catch (err: any) {
        console.error('Failed to fetch promotions data:', err);
        setError(err.message || 'Failed to load promotions data');
      } finally {
        setLoading(false);
      }
    };

    fetchPromotionsData();
  }, [brandId]);

  const uploadCampaignData = async (data: any) => {
    try {
      const response = await axios.post('/api/promotions/upload', {
        ...data,
        brand_id: brandId
      });
      return response.data;
    } catch (err) {
      console.error('Failed to upload campaign data:', err);
      throw err;
    }
  };

  const uploadMedia = async (file: File, campaignName: string) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('brand_id', brandId);
      formData.append('campaign_name', campaignName);

      const response = await axios.post('/api/promotions/upload-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to upload media:', err);
      throw err;
    }
  };

  return {
    summary,
    campaigns,
    channels,
    content,
    loading,
    error,
    uploadCampaignData,
    uploadMedia,
    refetch: () => {
      if (brandId) {
        // Re-trigger the effect by changing brandId
        const fetchPromotionsData = async () => {
          setLoading(true);
          setError(null);

          try {
            const [summaryRes, campaignsRes, channelsRes, contentRes] = await Promise.all([
              axios.get(`/api/promotions/summary/${brandId}`),
              axios.get(`/api/promotions/campaigns/${brandId}`),
              axios.get(`/api/promotions/channels/${brandId}`),
              axios.get(`/api/promotions/content/${brandId}`)
            ]);

            setSummary(summaryRes.data);
            setCampaigns(campaignsRes.data.campaigns || []);
            setChannels(channelsRes.data.channels || []);
            setContent(contentRes.data);
          } catch (err: any) {
            console.error('Failed to fetch promotions data:', err);
            setError(err.message || 'Failed to load promotions data');
          } finally {
            setLoading(false);
          }
        };

        fetchPromotionsData();
      }
    }
  };
}

export default usePromotions;